import React, { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Link,
    Stack,
    Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import { formatMoney } from "../../utils/helpers";

const LANGUAGE_LABELS = {
    en: "English",
    ru: "Russian",
    hy: "Armenian",
};

const CURRENCY_LABELS = {
    USD: "US Dollar (USD)",
    EUR: "Euro (EUR)",
    GBP: "British Pound (GBP)",
    AMD: "Armenian Dram (AMD)",
    RUB: "Russian Ruble (RUB)",
};

function userInitial(user) {
    const first = (user?.first_name || "").trim()[0];
    const last = (user?.last_name || "").trim()[0];
    return (first || last || "?").toUpperCase();
}

function formatDateTime(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatGender(value) {
    if (!value) return "—";
    if (value === "male") return "Male";
    if (value === "female") return "Female";
    return value;
}

function DetailRow({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                {value || "—"}
            </Typography>
        </Box>
    );
}

function EmptyState({ children }) {
    return (
        <Box
            sx={{
                py: 5,
                px: 3,
                textAlign: "center",
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {children}
            </Typography>
        </Box>
    );
}

export default function StudentProfile() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const response = await api.get(`admin/students/${id}/profile`);
                if (mounted) setData(response.data?.data ?? null);
            } catch (e) {
                if (mounted) setData(null);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 4 }}>
                <CircularProgress size={24} />
                <Typography sx={{ mt: 1 }}>Loading student profile...</Typography>
            </Box>
        );
    }

    if (!data?.student) {
        return (
            <Box sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 4 }}>
                <Typography>Student profile not found.</Typography>
            </Box>
        );
    }

    const student = data.student;
    const fullName = `${student.first_name || ""} ${student.last_name || ""}`.trim();
    const countryDisplay = student.country_name || student.country || "—";
    const languageDisplay = LANGUAGE_LABELS[student.language] || student.language || "—";
    const currencyDisplay = CURRENCY_LABELS[student.currency] || student.currency || "—";

    return (
        <Box sx={{ pt: 3, pb: 4, px: { xs: 2, sm: 3 }, maxWidth: 1600, mx: "auto" }}>
            <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Student Profile
            </Typography>

            <Box sx={{ display: "grid", gap: 2.5 }}>
                <Card variant="outlined">
                    <CardHeader title="Profile Info" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ pt: 2.5 }}>
                        <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", mb: 3 }}>
                            {student.avatar ? (
                                <Avatar src={student.avatar} alt={fullName || "Student"} sx={{ width: 88, height: 88 }} />
                            ) : (
                                <Avatar sx={{ width: 88, height: 88, fontSize: "1.75rem", fontWeight: 600 }}>
                                    {userInitial(student)}
                                </Avatar>
                            )}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {fullName || `Student #${student.id}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {student.email || "—"}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        size="small"
                                        label={student.email_verified_at ? "Email verified" : "Email not verified"}
                                        color={student.email_verified_at ? "success" : "default"}
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={student.phone_verified_at ? "Phone verified" : "Phone not verified"}
                                        color={student.phone_verified_at ? "success" : "default"}
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={student.is_moderated ? "Moderated" : "Not moderated"}
                                        color={student.is_moderated ? "success" : "warning"}
                                        variant="outlined"
                                    />
                                </Stack>
                            </Box>
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Personal
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <DetailRow label="First name" value={student.first_name} />
                            <DetailRow label="Last name" value={student.last_name} />
                            <DetailRow label="Phone" value={student.phone} />
                            <DetailRow label="Gender" value={formatGender(student.gender)} />
                            <DetailRow label="Date of birth" value={formatDate(student.dob)} />
                            <DetailRow label="Language" value={languageDisplay} />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Location
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <DetailRow label="Country" value={countryDisplay} />
                            <DetailRow label="City" value={student.city} />
                            <DetailRow label="State / Region" value={student.state} />
                            <DetailRow label="Address" value={student.address} />
                            <DetailRow label="ZIP / Postal code" value={student.zip} />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Preferences
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                                mb: student.bio ? 3 : 0,
                            }}
                        >
                            <DetailRow label="Timezone" value={student.timezone} />
                            <DetailRow label="Currency" value={currencyDisplay} />
                            <DetailRow label="Registered" value={formatDateTime(student.created_at)} />
                            <DetailRow label="Last updated" value={formatDateTime(student.updated_at)} />
                        </Box>

                        {student.bio ? (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Bio
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                    {student.bio}
                                </Typography>
                            </>
                        ) : null}
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardHeader title="Purchased Courses" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ p: 0 }}>
                        {!data.purchases?.length ? (
                            <EmptyState>No purchased courses.</EmptyState>
                        ) : (
                            <Stack spacing={0} divider={<Divider />}>
                                {data.purchases.map((purchase) => (
                                    <Box key={purchase.id} sx={{ p: 2.5 }}>
                                        <Typography sx={{ fontWeight: 600 }}>{purchase.course?.name || "—"}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Status: {purchase.status || "—"} • Lessons: {purchase.lessons_remaining ?? 0}/
                                            {purchase.lessons_total ?? 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            Purchased: {formatDateTime(purchase.created_at)} • Expires:{" "}
                                            {formatDateTime(purchase.expire_at)}
                                        </Typography>
                                        {purchase.classroom ? (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Classroom: {purchase.classroom.name} ({purchase.classroom.status || "—"})
                                            </Typography>
                                        ) : null}
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardHeader title="Orders & Transactions" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ p: 0 }}>
                        {!data.orders?.length ? (
                            <EmptyState>No orders found.</EmptyState>
                        ) : (
                            <Stack spacing={0} divider={<Divider />}>
                                {data.orders.map((order) => (
                                    <Box key={order.id} sx={{ p: 2.5 }}>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            Order #{order.id} • Amount: {order.amount != null ? formatMoney(order.amount) : "—"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Status: {order.status || "—"} • Payment: {order.payment_status || "—"} •{" "}
                                            {formatDateTime(order.created_at)}
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {!order.transactions?.length ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    No related transactions.
                                                </Typography>
                                            ) : (
                                                <Stack spacing={0.5}>
                                                    {order.transactions.map((tx) => (
                                                        <Typography key={tx.id} variant="caption" color="text.secondary">
                                                            TX #{tx.id} ({tx.transaction_id || "—"}) • {tx.method || "—"} •{" "}
                                                            {tx.status || "—"} • {tx.amount != null ? formatMoney(tx.amount) : "—"} • {formatDateTime(tx.created_at)}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardHeader title="Classrooms" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ p: 0 }}>
                        {!data.classrooms?.length ? (
                            <EmptyState>No classrooms linked.</EmptyState>
                        ) : (
                            <Stack spacing={0} divider={<Divider />}>
                                {data.classrooms.map((classroom) => (
                                    <Box
                                        key={classroom.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 2,
                                            p: 2.5,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontWeight: 600 }}>{classroom.name}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {classroom.course_name || "—"} • {classroom.status || "—"}
                                            </Typography>
                                        </Box>
                                        <Link
                                            href={`/classrooms/${classroom.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                        >
                                            Open classroom
                                        </Link>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
